import type {
    EmployeeRequest,
    OrgEmployeeRequest,
    PutAvailabilityPattern,
    RestAvailabilityPattern,
    SegmentType,
} from '@shared/models/requestTypes';

import {
    type Request,
    type RequestTransaction,
    RequestSource,
    RequestStatus,
} from '@features/requests/types';

/**
 * Request Mappers
 *
 * Pure functions that translate between the API model
 * (`EmployeeRequest` / `OrgEmployeeRequest` / `PutAvailabilityPattern`) and
 * the feature's in-memory `Request` model (which now uses the canonical API
 * types: `RequestSource`, `RequestStatus`, `SegmentType`).
 *
 * These were previously hosted inside `requestStore`; extracting them keeps
 * the store focused on state + domain transitions (SRP) and removes the
 * store's dependency on the API model types.
 */

/**
 * Parses a date string, detecting format automatically
 * Supports both ISO YYYY-MM-DD and DD-MM-YYYY formats
 */
function parseDateFlexible(dateStr: string): Date {
    const parts = dateStr.split('-').map(Number);

    // If first part is >= 1000, it's likely YYYY (ISO format: YYYY-MM-DD)
    if (parts.length === 3 && parts[0] >= 1000) {
        return new Date(parts[0], parts[1] - 1, parts[2]);
    }
    // Otherwise, assume DD-MM-YYYY format
    return new Date(parts[2], parts[1] - 1, parts[0]);
}

/**
 * Normalizes request dates from startPeriods array
 * Supports both DD-MM-YYYY and YYYY-MM-DD formats
 */
function normalizeRequestDates(startPeriods: string[] | undefined): {
    startDate: Date | null;
    endDate: Date | null;
} {
    if (!startPeriods || startPeriods.length === 0) {
        return { startDate: null, endDate: null };
    }

    // Sort the startPeriods array to ensure we get the correct first and last dates
    // This handles cases where the API returns dates in descending order (e.g., 30, 29, 28... 25)
    const sortedPeriods = [...(startPeriods || [])].sort((a, b): number => {
        const dateA = a.includes('-') ? parseDateFlexible(a) : new Date(a);
        const dateB = b.includes('-') ? parseDateFlexible(b) : new Date(b);
        return dateA.getTime() - dateB.getTime();
    });

    // Parse first date (now guaranteed to be the earliest)
    const firstDateStr = sortedPeriods[0];
    let startDate: Date;

    if (firstDateStr.includes('-')) {
        startDate = parseDateFlexible(firstDateStr);
    } else {
        startDate = new Date(firstDateStr);
    }

    // Parse last date (now guaranteed to be the latest)
    const lastDateStr = sortedPeriods[sortedPeriods.length - 1];
    let endDate: Date;

    if (lastDateStr.includes('-')) {
        endDate = parseDateFlexible(lastDateStr);
    } else {
        endDate = new Date(lastDateStr);
    }

    return { startDate, endDate };
}

/**
 * Converts string dates to Date objects
 * Supports both ISO YYYY-MM-DD and DD-MM-YYYY formats
 */
function parseDateStrings(dates: string[] | undefined): Date[] {
    if (!dates || dates.length === 0) {
        return [];
    }
    return dates.map((dateStr): Date => {
        if (dateStr.includes('-')) {
            return parseDateFlexible(dateStr);
        }
        return new Date(dateStr);
    });
}

/**
 * Extracts excludedDates from availabilityPattern
 * excludedDates are dates the employee explicitly excludes from the pattern
 * Note: Currently, the API doesn't have a separate field for excludedDates,
 * so we return undefined. In the future, this might come from a different field.
 */
function getExcludedDatesFromPattern(
    pattern: RestAvailabilityPattern | PutAvailabilityPattern,
): Date[] | undefined {
    if (!pattern?.excludePeriods) {
        return undefined;
    }

    // Convert excludePeriods strings to Date objects
    // In the API, excludePeriods contains dates that the employee has excluded
    // Supports both ISO YYYY-MM-DD and DD-MM-YYYY formats
    return pattern.excludePeriods.map((dateStr: string): Date => {
        if (dateStr.includes('-')) {
            return parseDateFlexible(dateStr);
        }
        return new Date(dateStr);
    });
}

/**
 * Converts an EmployeeRequest to the legacy Request format
 */
export function toLegacyRequest(request: EmployeeRequest): Request {
    // `source` from the API is already canonical (matches RequestSource).
    const type = request.source;

    let startDate: Date | null = null;
    let endDate: Date | null = null;
    const deadline = request.deadline ? new Date(request.deadline) : undefined;
    const excludedPeriods = parseDateStrings(
        request.availabilityPattern.excludePeriods,
    );
    const excludedDates = getExcludedDatesFromPattern(
        request.availabilityPattern,
    );

    // For DRAFT requests without startPeriods, dates remain null — no default
    // dates for drafts without dates. The deadline is only for validation,
    // not for setting default dates.
    if (
        request.status !== RequestStatus.DRAFT ||
        (request.availabilityPattern.startPeriods &&
            request.availabilityPattern.startPeriods.length > 0)
    ) {
        // Use series.from and series.until if available, otherwise fall back to startPeriods
        if (
            request.availabilityPattern.series?.from &&
            request.availabilityPattern.series?.until
        ) {
            startDate = new Date(request.availabilityPattern.series.from);
            endDate = new Date(request.availabilityPattern.series.until);
        } else {
            const normalized = normalizeRequestDates(
                request.availabilityPattern.startPeriods,
            );
            startDate = normalized.startDate;
            endDate = normalized.endDate;
        }
    }

    // Status from the API is already canonical (matches the shared RequestStatus
    // type). No mapping needed.
    const status = request.status;

    // Convert transactions: registered comes as string from API, legacy expects Date
    const transactions = request.transactions?.map((t): RequestTransaction => ({
        id: t.id,
        registered: new Date(t.registered),
        comment: t.comment,
        status: t.status,
        authId: t.authId ?? '',
    }));

    return {
        id: request.id,
        type,
        status,
        startDate,
        endDate,
        notes: '',
        attachments: [],
        createdAt: new Date(request.registered),
        updatedAt: new Date(request.updated),
        history: [],
        employeeId: request.employeeId,
        deadline,
        excludedPeriods,
        excludedDates,
        transactions,
    };
}

/**
 * Converts an array of EmployeeRequest to legacy Request array
 */
export function toLegacyRequests(requests: EmployeeRequest[]): Request[] {
    return requests.map(toLegacyRequest);
}

/**
 * Converts OrgEmployeeRequest to EmployeeRequest
 */
export function toEmployeeRequest(
    orgRequest: OrgEmployeeRequest,
): EmployeeRequest {
    return {
        ...orgRequest,
        employeeId: orgRequest.orgEmployeeId,
    };
}

/**
 * Converts legacy Request to API PutAvailabilityPattern format
 * Used for updating requests via the API
 */
export function toApiAvailabilityPattern(
    request: Request,
): PutAvailabilityPattern {
    // Convert dates to ISO string format (YYYY-MM-DD)
    const formatDate = (date?: Date): string | undefined => {
        if (!date) return undefined;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Build startPeriods - include ALL dates in the range
    const startPeriods: string[] = [];
    if (request.startDate && request.endDate) {
        const start = new Date(request.startDate);
        const end = new Date(request.endDate);

        // Normalize dates to start of day for accurate comparison
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);

        // Iterate through each day in the range
        for (
            let current = new Date(start);
            current <= end;
            current.setDate(current.getDate() + 1)
        ) {
            const dateStr = formatDate(current);
            if (dateStr) {
                startPeriods.push(dateStr);
            }
        }
    } else if (request.startDate) {
        const dateStr = formatDate(request.startDate);
        if (dateStr) {
            startPeriods.push(dateStr);
        }
    }

    // Build excludePeriods from excludedDates
    const excludePeriods: string[] = (request.excludedDates ?? [])
        .map(formatDate)
        .filter((d): d is string => d !== undefined);

    // For now, use a simple segment pattern based on source.
    // SHIFT_PREFERENCE UI category corresponds to the `WISH` API source.
    // This should be adjusted based on actual requirements
    const segmentPattern: SegmentType[][] =
        request.type === RequestSource.WISH && request.shiftPreference
            ? [[request.shiftPreference]]
            : ([['X']] as SegmentType[][]); // Default to X as per API requirements

    const result: PutAvailabilityPattern = {
        summary: request.notes ?? '',
        segmentPattern,
        startPeriods,
        excludePeriods,
        disabled: false,
        optimize: false,
    };

    return result;
}

/**
 * Updates or adds a request to the requests array (immutable).
 * Used by the store and by hooks that push fresh API data into the store.
 */
export function updateOrAddRequest(
    stateRequests: Request[],
    request: Request,
): Request[] {
    const existingIndex = stateRequests.findIndex(
        (r): boolean => r.id === request.id,
    );
    if (existingIndex !== -1) {
        return stateRequests.map((r, i): Request =>
            i === existingIndex ? request : r,
        );
    }
    return [...stateRequests, request];
}
